// guitarEffects.js - Refactored Web Audio API Version
import * as Tone from 'tone';
import { errorLogger } from './errorLogger.js';
import { audioNormalizer } from './audioNormalizer.js';

class GuitarEffectsProcessor {
    constructor() {
        this.isInitialized = false;
        this.audioContext = null;
        this.input = null;
        this.micSource = null;
        this.effectsChain = {};
        this.isEffectsEnabled = true;
        this.currentPreset = null;
        this.workletLoaded = false;

        // Новые свойства для управления эффектами
        this.effectsEnabled = {
            noiseGate: true,
            distortion: true,
            filter: true,
            delay: true,
            compressor: true,
            eq: true,
            reverb: true
        };

        this.effectParams = {
            noiseGate: { threshold: 0.02, releaseTime: 0.1 },
            distortion: { distortion: 0.5, gain: 0.2 },
            filter: { type: 'lowpass', frequency: 2670, Q: 0.4 },
            delay: { wet: 0.3, delayTime: 0.2, feedback: 0.4 },
            compressor: { threshold: -36, ratio: 4, attack: 0.003, release: 0.25 },
            eq: { gains: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
            reverb: { wet: 0.72, decay: 2 },
            inputGain: 1.0,
            outputGain: 1.0
        };

        // Настройки эффектов теперь загружаются только через API (loadEffectsFromServer)
        // Не загружаем из localStorage

        // DOM элементы
        this.elements = this.getDOMElements();
        this.setupEventListeners();
    }

    // Метод для получения всех текущих настроек
    getAllSettings() {
        return {
            effectsEnabled: { ...this.effectsEnabled },
            effectParams: { ...this.effectParams },
            isEffectsEnabled: this.isEffectsEnabled,
            currentPreset: this.currentPreset
        };
    }

    // Метод для применения сохраненных настроек
    applyAllSettings(settings) {
        if (!settings) return;

        try {
            // Восстанавливаем состояния эффектов
            if (settings.effectsEnabled) {
                this.effectsEnabled = { ...settings.effectsEnabled };
            }

            // Восстанавливаем параметры эффектов
            if (settings.effectParams) {
                this.effectParams = { ...settings.effectParams };
            }

            // Восстанавливаем общее состояние
            if (settings.isEffectsEnabled !== undefined) {
                this.isEffectsEnabled = settings.isEffectsEnabled;
            }

            // Восстанавливаем текущий пресет
            if (settings.currentPreset) {
                this.currentPreset = settings.currentPreset;
            }

            // Применяем параметры к эффектам
            this.applyEffectParams();

            // Обновляем UI
            this.updateAdvancedUI();

            // Обновляем состояние переключателей
            this.updateEffectToggles();

            console.log('Settings applied successfully');

        } catch (error) {
            console.error('Error applying settings:', error);
            errorLogger.logCustomError(
                `Error applying guitar effects settings: ${error.message}`,
                error.stack,
                { errorName: error.name }
            );
        }
    }

    // Применяем параметры к эффектам
    applyEffectParams() {
        if (!this.isInitialized) return;

        // Применяем все параметры к соответствующим эффектам
        this.updateNoiseGateParam('threshold', this.effectParams.noiseGate.threshold);
        this.updateNoiseGateParam('releaseTime', this.effectParams.noiseGate.releaseTime);

        this.updateDistortionParam('distortion', this.effectParams.distortion.distortion);
        this.updateDistortionParam('gain', this.effectParams.distortion.gain);

        this.updateFilterParam('type', this.effectParams.filter.type);
        this.updateFilterParam('frequency', this.effectParams.filter.frequency);
        this.updateFilterParam('Q', this.effectParams.filter.Q);

        this.updateDelayParam('wet', this.effectParams.delay.wet);
        this.updateDelayParam('delayTime', this.effectParams.delay.delayTime);
        this.updateDelayParam('feedback', this.effectParams.delay.feedback);

        this.updateCompressorParam('threshold', this.effectParams.compressor.threshold);
        this.updateCompressorParam('ratio', this.effectParams.compressor.ratio);
        this.updateCompressorParam('attack', this.effectParams.compressor.attack);
        this.updateCompressorParam('release', this.effectParams.compressor.release);

        // Эквалайзер
        this.effectParams.eq.gains.forEach((gain, band) => {
            this.updateEQParam(band, gain);
        });

        this.updateReverbParam('wet', this.effectParams.reverb.wet);
        this.updateReverbParam('decay', this.effectParams.reverb.decay);

        this.updateInputGain(this.effectParams.inputGain);
        this.updateOutputGain(this.effectParams.outputGain);

        // Обновляем цепочку эффектов
        this.updateEffectsChain();
    }

    // Обновляем состояние переключателей
    updateEffectToggles() {
        const toggleIds = {
            noiseGate: 'noiseGateEnabled',
            distortion: 'distortionEnabled',
            filter: 'filterEnabled',
            delay: 'delayEnabled',
            compressor: 'compressorEnabled',
            eq: 'eqEnabled',
            reverb: 'reverbEnabled'
        };

        Object.entries(toggleIds).forEach(([effect, elementId]) => {
            const checkbox = document.getElementById(elementId);
            if (checkbox) {
                checkbox.checked = this.effectsEnabled[effect];
            }
        });

        // НЕ обновляем общий переключатель эффектов, чтобы пользователь мог управлять им вручную
        // Общий переключатель эффектов должен изменяться только пользователем
    }

    // Сохраняем настройки в localStorage
    saveSettings() {
        try {
            const settings = this.getAllSettings();
            localStorage.setItem('guitarEffectsSettings', JSON.stringify(settings));
            console.log('Settings saved successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            errorLogger.logCustomError(
                `Error saving guitar effects settings: ${error.message}`,
                error.stack,
                { errorName: error.name }
            );
        }
    }

    // Загружаем настройки из localStorage
    loadSettings() {
        try {
            const saved = localStorage.getItem('guitarEffectsSettings');
            if (saved) {
                const settings = JSON.parse(saved);
                this.applyAllSettings(settings);
                console.log('Settings loaded successfully');
                return true;
            }
        } catch (error) {
            console.error('Error loading settings:', error);
            errorLogger.logCustomError(
                `Error loading guitar effects settings: ${error.message}`,
                error.stack,
                { errorName: error.name }
            );
        }
        return false;
    }
    
    // Загрузить эффекты с сервера для конкретной песни/блока
    async loadEffectsFromServer(songId, blockId = null) {
        try {
            const params = new URLSearchParams();
            if (songId) params.append('song_id', songId);
            if (blockId) params.append('block_id', blockId);
            
            const response = await fetch(`/api/sound-effects/effective?${params}`);
            if (!response.ok) {
                console.warn('Failed to load sound effects from server');
                return false;
            }
            
            const effect = await response.json();
            if (!effect || !effect.id) {
                console.log('No custom effect found, using local settings');
                return false;
            }
            
            console.log('Applying server sound effect:', effect.name);
            this.applyServerEffect(effect);
            return true;
        } catch (error) {
            // Не логируем ошибки, вызванные расширениями/антивирусом (например, Kaspersky)
            const errorStack = error.stack || '';
            const isExtensionError = errorStack.toLowerCase().includes('kaspersky') || 
                                   errorStack.toLowerCase().includes('extension');
            
            if (isExtensionError) {
                console.warn('Failed to load sound effects from server (blocked by browser extension/antivirus):', error.message);
                // Не отправляем на сервер, просто возвращаем false
                return false;
            }
            
            console.error('Error loading effects from server:', error);
            errorLogger.logCustomError(
                `Error loading effects from server: ${error.message}`,
                error.stack,
                { songId, blockId, errorName: error.name }
            );
            return false;
        }
    }
    
    // Применить эффект с сервера
    applyServerEffect(effect) {
        if (!effect) return;
        
        try {
            // Парсим JSON если нужно
            const effectsEnabled = typeof effect.effects_enabled === 'string' 
                ? JSON.parse(effect.effects_enabled) 
                : effect.effects_enabled;
            const effectParams = typeof effect.effect_params === 'string' 
                ? JSON.parse(effect.effect_params) 
                : effect.effect_params;
            
            // Если эффекты еще не инициализированы, сохраняем настройки для применения позже
            if (!this.isInitialized || !this.effectsChain || !this.effectsChain.distortion) {
                console.log('Effects not initialized yet, saving settings for later application');
                // Сохраняем настройки в effectParams для применения при инициализации
                if (effectsEnabled) {
                    Object.keys(effectsEnabled).forEach(key => {
                        if (this.effectsEnabled.hasOwnProperty(key)) {
                            this.effectsEnabled[key] = effectsEnabled[key];
                        }
                    });
                }
                if (effectParams) {
                    // Сохраняем параметры в this.effectParams
                    if (effectParams.distortion) {
                        if (effectParams.distortion.distortion !== undefined) {
                            this.effectParams.distortion.distortion = effectParams.distortion.distortion;
                        }
                        if (effectParams.distortion.gain !== undefined) {
                            this.effectParams.distortion.gain = effectParams.distortion.gain;
                        }
                    }
                    if (effectParams.filter) {
                        if (effectParams.filter.frequency !== undefined) {
                            this.effectParams.filter.frequency = effectParams.filter.frequency;
                        }
                        if (effectParams.filter.Q !== undefined) {
                            this.effectParams.filter.Q = effectParams.filter.Q;
                        }
                        if (effectParams.filter.type !== undefined) {
                            this.effectParams.filter.type = effectParams.filter.type;
                        }
                    }
                    if (effectParams.delay) {
                        if (effectParams.delay.wet !== undefined) {
                            this.effectParams.delay.wet = effectParams.delay.wet;
                        }
                        if (effectParams.delay.delayTime !== undefined) {
                            this.effectParams.delay.delayTime = effectParams.delay.delayTime;
                        }
                        if (effectParams.delay.feedback !== undefined) {
                            this.effectParams.delay.feedback = effectParams.delay.feedback;
                        }
                    }
                    if (effectParams.compressor) {
                        Object.keys(effectParams.compressor).forEach(key => {
                            if (this.effectParams.compressor.hasOwnProperty(key)) {
                                this.effectParams.compressor[key] = effectParams.compressor[key];
                            }
                        });
                    }
                    if (effectParams.eq && effectParams.eq.gains) {
                        this.effectParams.eq.gains = [...effectParams.eq.gains];
                    }
                    if (effectParams.reverb) {
                        if (effectParams.reverb.wet !== undefined) {
                            this.effectParams.reverb.wet = effectParams.reverb.wet;
                        }
                        if (effectParams.reverb.decay !== undefined) {
                            this.effectParams.reverb.decay = effectParams.reverb.decay;
                        }
                    }
                    if (effectParams.noiseGate) {
                        if (effectParams.noiseGate.threshold !== undefined) {
                            this.effectParams.noiseGate.threshold = effectParams.noiseGate.threshold;
                        }
                        if (effectParams.noiseGate.releaseTime !== undefined) {
                            this.effectParams.noiseGate.releaseTime = effectParams.noiseGate.releaseTime;
                        }
                    }
                    if (effectParams.inputGain !== undefined) {
                        this.effectParams.inputGain = effectParams.inputGain;
                    }
                    if (effectParams.outputGain !== undefined) {
                        this.effectParams.outputGain = effectParams.outputGain;
                    }
                }
                return; // Выходим, настройки будут применены при инициализации
            }
            
            // Применяем включение/выключение эффектов
            if (effectsEnabled) {
                Object.keys(effectsEnabled).forEach(key => {
                    if (this.effectsEnabled.hasOwnProperty(key)) {
                        this.effectsEnabled[key] = effectsEnabled[key];
                        this.toggleEffect(key, effectsEnabled[key]);
                    }
                });
            }
            
            // Применяем параметры эффектов
            if (effectParams) {
                // Distortion
                if (effectParams.distortion) {
                    if (effectParams.distortion.distortion !== undefined) {
                        this.updateDistortionParam('distortion', effectParams.distortion.distortion);
                    }
                    if (effectParams.distortion.gain !== undefined) {
                        this.updateDistortionParam('gain', effectParams.distortion.gain);
                    }
                }
                
                // Filter
                if (effectParams.filter) {
                    if (effectParams.filter.frequency !== undefined) {
                        this.updateFilterParam('frequency', effectParams.filter.frequency);
                    }
                    if (effectParams.filter.Q !== undefined) {
                        this.updateFilterParam('Q', effectParams.filter.Q);
                    }
                }
                
                // Delay
                if (effectParams.delay) {
                    if (effectParams.delay.wet !== undefined) {
                        this.updateDelayParam('wet', effectParams.delay.wet);
                    }
                    if (effectParams.delay.delayTime !== undefined) {
                        this.updateDelayParam('delayTime', effectParams.delay.delayTime);
                    }
                    if (effectParams.delay.feedback !== undefined) {
                        this.updateDelayParam('feedback', effectParams.delay.feedback);
                    }
                }
                
                // Compressor
                if (effectParams.compressor) {
                    if (effectParams.compressor.threshold !== undefined) {
                        this.updateCompressorParam('threshold', effectParams.compressor.threshold);
                    }
                    if (effectParams.compressor.ratio !== undefined) {
                        this.updateCompressorParam('ratio', effectParams.compressor.ratio);
                    }
                }
                
                // EQ
                if (effectParams.eq) {
                    if (effectParams.eq.low !== undefined) {
                        this.updateEQParam(0, effectParams.eq.low);
                    }
                    if (effectParams.eq.mid !== undefined) {
                        this.updateEQParam(4, effectParams.eq.mid);
                    }
                    if (effectParams.eq.high !== undefined) {
                        this.updateEQParam(9, effectParams.eq.high);
                    }
                }
                
                // Reverb
                if (effectParams.reverb) {
                    if (effectParams.reverb.wet !== undefined) {
                        this.updateReverbParam('wet', effectParams.reverb.wet);
                    }
                    if (effectParams.reverb.decay !== undefined) {
                        this.updateReverbParam('decay', effectParams.reverb.decay);
                    }
                }
                
                // Noise Gate
                if (effectParams.noiseGate) {
                    if (effectParams.noiseGate.threshold !== undefined) {
                        this.updateNoiseGateParam('threshold', effectParams.noiseGate.threshold);
                    }
                }
            }
            
            // Обновляем UI (если методы существуют и мы в игровом режиме)
            // В админке UI элементы отсутствуют, поэтому эти методы не нужны
            if (typeof this.updateAdvancedUI === 'function') {
                this.updateAdvancedUI();
            }
            if (typeof this.updateEffectToggles === 'function') {
                this.updateEffectToggles();
            }
            
            console.log('Server effect applied successfully:', effect.name);
        } catch (error) {
            console.error('Error applying server effect:', error);
            errorLogger.logCustomError(
                `Error applying server effect: ${error.message}`,
                error.stack,
                { 
                    effectName: effect?.name || 'unknown',
                    effectId: effect?.id || null,
                    errorName: error.name,
                    effectsEnabled: effectsEnabled,
                    effectParams: effectParams
                }
            );
        }
    }

    getDOMElements() {
        return {
            effectsToggle: document.getElementById('effectsToggle')
        };
    }

    setupEventListeners() {
        const { effectsToggle } = this.elements;

        if (effectsToggle) effectsToggle.addEventListener('change', (e) => this.toggleEffects(e.target.checked));

        this.setupAdvancedEffectListeners();
    }

    setupAdvancedEffectListeners() {
        // Включение/выключение эффектов (проверяем существование элементов)
        const noiseGateEnabled = document.getElementById('noiseGateEnabled');
        if (noiseGateEnabled) noiseGateEnabled.addEventListener('change', (e) => this.toggleEffect('noiseGate', e.target.checked));
        
        const distortionEnabled = document.getElementById('distortionEnabled');
        if (distortionEnabled) distortionEnabled.addEventListener('change', (e) => this.toggleEffect('distortion', e.target.checked));
        
        const filterEnabled = document.getElementById('filterEnabled');
        if (filterEnabled) filterEnabled.addEventListener('change', (e) => this.toggleEffect('filter', e.target.checked));
        
        const delayEnabled = document.getElementById('delayEnabled');
        if (delayEnabled) delayEnabled.addEventListener('change', (e) => this.toggleEffect('delay', e.target.checked));
        
        const compressorEnabled = document.getElementById('compressorEnabled');
        if (compressorEnabled) compressorEnabled.addEventListener('change', (e) => this.toggleEffect('compressor', e.target.checked));
        
        const eqEnabled = document.getElementById('eqEnabled');
        if (eqEnabled) eqEnabled.addEventListener('change', (e) => this.toggleEffect('eq', e.target.checked));
        
        const reverbEnabled = document.getElementById('reverbEnabled');
        if (reverbEnabled) reverbEnabled.addEventListener('change', (e) => this.toggleEffect('reverb', e.target.checked));

        // Параметры шумоподавителя
        const noiseGateThreshold = document.getElementById('noiseGateThreshold');
        if (noiseGateThreshold) noiseGateThreshold.addEventListener('input', (e) => this.updateNoiseGateParam('threshold', parseFloat(e.target.value)));
        
        const noiseGateRelease = document.getElementById('noiseGateRelease');
        if (noiseGateRelease) noiseGateRelease.addEventListener('input', (e) => this.updateNoiseGateParam('releaseTime', parseFloat(e.target.value)));

        // Параметры дисторшна
        const distortionAmount = document.getElementById('distortionAmount');
        if (distortionAmount) distortionAmount.addEventListener('input', (e) => this.updateDistortionParam('distortion', parseFloat(e.target.value)));
        
        const distortionGain = document.getElementById('distortionGain');
        if (distortionGain) distortionGain.addEventListener('input', (e) => this.updateDistortionParam('gain', parseFloat(e.target.value)));

        // Параметры фильтра
        const filterType = document.getElementById('filterType');
        if (filterType) filterType.addEventListener('change', (e) => this.updateFilterParam('type', e.target.value));
        
        const filterFrequency = document.getElementById('filterFrequency');
        if (filterFrequency) filterFrequency.addEventListener('input', (e) => this.updateFilterParam('frequency', parseFloat(e.target.value)));
        
        const filterQ = document.getElementById('filterQ');
        if (filterQ) filterQ.addEventListener('input', (e) => this.updateFilterParam('Q', parseFloat(e.target.value)));

        // Параметры дилея
        const delayWet = document.getElementById('delayWet');
        if (delayWet) delayWet.addEventListener('input', (e) => this.updateDelayParam('wet', parseFloat(e.target.value)));
        
        const delayTime = document.getElementById('delayTime');
        if (delayTime) delayTime.addEventListener('input', (e) => this.updateDelayParam('delayTime', parseFloat(e.target.value)));
        
        const delayFeedback = document.getElementById('delayFeedback');
        if (delayFeedback) delayFeedback.addEventListener('input', (e) => this.updateDelayParam('feedback', parseFloat(e.target.value)));

        // Параметры компрессора
        const compressorThreshold = document.getElementById('compressorThreshold');
        if (compressorThreshold) compressorThreshold.addEventListener('input', (e) => this.updateCompressorParam('threshold', parseFloat(e.target.value)));
        
        const compressorRatio = document.getElementById('compressorRatio');
        if (compressorRatio) compressorRatio.addEventListener('input', (e) => this.updateCompressorParam('ratio', parseFloat(e.target.value)));
        
        const compressorAttack = document.getElementById('compressorAttack');
        if (compressorAttack) compressorAttack.addEventListener('input', (e) => this.updateCompressorParam('attack', parseFloat(e.target.value)));
        
        const compressorRelease = document.getElementById('compressorRelease');
        if (compressorRelease) compressorRelease.addEventListener('input', (e) => this.updateCompressorParam('release', parseFloat(e.target.value)));

        // Параметры эквалайзера
        const eqBands = document.querySelectorAll('.eq-band');
        if (eqBands.length > 0) {
            eqBands.forEach(slider => {
                slider.addEventListener('input', (e) => {
                    const band = parseInt(e.target.dataset.band);
                    const gain = parseFloat(e.target.value);
                    this.updateEQParam(band, gain);
                });
            });
        }

        // Параметры реверберации
        const reverbWet = document.getElementById('reverbWet');
        if (reverbWet) reverbWet.addEventListener('input', (e) => this.updateReverbParam('wet', parseFloat(e.target.value)));
        
        const reverbDecay = document.getElementById('reverbDecay');
        if (reverbDecay) reverbDecay.addEventListener('input', (e) => this.updateReverbParam('decay', parseFloat(e.target.value)));

        // Общие параметры
        const inputGain = document.getElementById('inputGain');
        if (inputGain) inputGain.addEventListener('input', (e) => this.updateInputGain(parseFloat(e.target.value)));
        
        const outputGain = document.getElementById('outputGain');
        if (outputGain) outputGain.addEventListener('input', (e) => this.updateOutputGain(parseFloat(e.target.value)));

        // Обновление значений параметров в реальном времени
        this.setupParamValueUpdaters();

        // Добавьте обработчики для кнопок управления настройками
        document.getElementById('saveSettingsBtn')?.addEventListener('click', () => {
            this.saveSettings();
            this.showNotification('Настройки сохранены!');
        });

        document.getElementById('loadSettingsBtn')?.addEventListener('click', () => {
            if (this.loadSettings()) {
                this.showNotification('Настройки загружены!');
            } else {
                this.showNotification('Сохраненные настройки не найдены', 'error');
            }
        });

        document.getElementById('resetSettingsBtn')?.addEventListener('click', () => {
            if (confirm('Вы уверены, что хотите сбросить все настройки эффектов к стандартным?')) {
                this.resetToDefaults();
                this.showNotification('Настройки сброшены к стандартным!');
            }
        });
    }

    // Метод для сброса к стандартным настройкам
    resetToDefaults() {
        this.effectsEnabled = {
            noiseGate: true,
            distortion: true,
            filter: true,
            delay: true,
            compressor: true,
            eq: true,
            reverb: true
        };

        this.effectParams = {
            noiseGate: { threshold: 0.02, releaseTime: 0.1 },
            distortion: { distortion: 0.5, gain: 0.2 },
            filter: { type: 'lowpass', frequency: 2670, Q: 0.4 },
            delay: { wet: 0.3, delayTime: 0.2, feedback: 0.4 },
            compressor: { threshold: -36, ratio: 4, attack: 0.003, release: 0.25 },
            eq: { gains: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
            reverb: { wet: 0.72, decay: 2 },
            inputGain: 1.0,
            outputGain: 1.0
        };

        this.isEffectsEnabled = true;

        // Применяем стандартные настройки
        this.applyEffectParams();
        this.updateAdvancedUI();
        this.updateEffectToggles();

        // Сохраняем стандартные настройки
        this.saveSettings();
    }

    // Вспомогательный метод для показа уведомлений
    showNotification(message, type = 'success') {
        // Создаем временное уведомление
        const notification = document.createElement('div');
        notification.textContent = message;
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 15px 20px;
            background: ${type === 'success' ? '#4CAF50' : '#ff5252'};
            color: white;
            border-radius: 5px;
            z-index: 10000;
            font-weight: bold;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 3000);
    }

    // Обновите метод setupParamValueUpdaters для немедленного обновления значений
    setupParamValueUpdaters() {
        const updateValue = (sliderId, valueId) => {
            const slider = document.getElementById(sliderId);
            const valueDisplay = document.getElementById(valueId);
            if (slider && valueDisplay) {
                // Обновляем значение сразу
                valueDisplay.textContent = slider.value;

                // И добавляем обработчик для будущих изменений
                slider.addEventListener('input', (e) => {
                    valueDisplay.textContent = e.target.value;
                });
            }
        };

        // Обновляем все ползунки
        updateValue('noiseGateThreshold', 'noiseGateThresholdValue');
        updateValue('noiseGateRelease', 'noiseGateReleaseValue');
        updateValue('distortionAmount', 'distortionAmountValue');
        updateValue('distortionGain', 'distortionGainValue');
        updateValue('filterFrequency', 'filterFrequencyValue');
        updateValue('filterQ', 'filterQValue');
        updateValue('delayWet', 'delayWetValue');
        updateValue('delayTime', 'delayTimeValue');
        updateValue('delayFeedback', 'delayFeedbackValue');
        updateValue('compressorThreshold', 'compressorThresholdValue');
        updateValue('compressorRatio', 'compressorRatioValue');
        updateValue('compressorAttack', 'compressorAttackValue');
        updateValue('compressorRelease', 'compressorReleaseValue');
        updateValue('reverbWet', 'reverbWetValue');
        updateValue('reverbDecay', 'reverbDecayValue');
        updateValue('inputGain', 'inputGainValue');
        updateValue('outputGain', 'outputGainValue');
    }

    toggleEffect(effectName, enabled) {
        if (!this.effectsChain[effectName]) {
            console.warn(`Effect ${effectName} not found`);
            return;
        }

        this.effectsEnabled[effectName] = enabled;
        this.updateEffectsChain();
        console.log(`${effectName} ${enabled ? 'enabled' : 'disabled'}`);
    }

    updateEffectsChain() {
        if (!this.isInitialized) return;

        // Отключаем все соединения
        this.disconnectEffectsChain();

        // Собираем цепочку заново с учетом включенных эффектов
        let previousNode = this.input;

        const effectOrder = ['noiseGate', 'distortion', 'filter', 'delay', 'compressor', 'eq', 'reverb'];

        for (const effectName of effectOrder) {
            if (this.effectsEnabled[effectName] && this.effectsChain[effectName]) {
                const effect = this.effectsChain[effectName];

                if (effectName === 'delay') {
                    previousNode.connect(effect.input);
                    previousNode = effect.output;
                } else if (effectName === 'eq') {
                    previousNode.connect(effect.input);
                    previousNode = effect.output;
                } else if (effectName === 'reverb') {
                    previousNode.connect(effect.input);
                    previousNode = effect.output;
                } else {
                    previousNode.connect(effect);
                    previousNode = effect;
                }
            }
        }

        // Подключаем к выходу
        previousNode.connect(this.effectsChain.output);
        
        // Подключаем к нормализатору вместо прямого подключения к destination
        audioNormalizer.initialize(this.audioContext);
        audioNormalizer.normalizeGuitar(this.effectsChain.output);
        const normalizerInput = audioNormalizer.getWebAudioInput(this.audioContext);
        if (normalizerInput) {
            this.effectsChain.output.connect(normalizerInput);
        } else {
            // Fallback: прямое подключение, если нормализатор не работает
            this.effectsChain.output.connect(this.audioContext.destination);
        }

        console.log('Effects chain updated with enabled effects:', this.effectsEnabled);

    }

    // Методы для обновления параметров эффектов
    updateNoiseGateParam(param, value) {
        // Clamp threshold value to valid range [0.001, 0.1]
        if (param === 'threshold') {
            value = Math.max(0.001, Math.min(0.1, value));
        }
        
        this.effectParams.noiseGate[param] = value;
        if (this.effectsChain.noiseGate && this.effectsChain.noiseGate.parameters) {
            try {
                this.effectsChain.noiseGate.parameters.get(param).value = value;
            } catch (error) {
                console.log('Noise gate parameter update not supported, using fallback');
            }
        }

    }

    makeDistortionCurve(amount) {
        const k = typeof amount === 'number' ? amount : 50;
        const n_samples = 44100;
        const curve = new Float32Array(n_samples);
        const deg = Math.PI / 180;

        for (let i = 0; i < n_samples; i++) {
            const x = i * 2 / n_samples - 1;
            curve[i] = (3 + k) * x * 20 * deg / (Math.PI + k * Math.abs(x));
        }
        return curve;
    }

    updateDistortionParam(param, value) {
        this.effectParams.distortion[param] = value;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.distortion) {
            console.log('Effects chain not initialized, distortion parameter saved for later');
            return;
        }

        if (param === 'distortion') {
            this.effectsChain.distortion.curve = this.makeDistortionCurve(value * 100);
        } else if (param === 'gain') {
            // Для громкости дисторшна нужно создать GainNode перед/после или настроить выходной уровень
            console.log('Distortion gain parameter updated to:', value);
        }
    }

    updateFilterParam(param, value) {
        this.effectParams.filter[param] = value;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.filter) {
            console.log('Effects chain not initialized, filter parameter saved for later');
            return;
        }

        if (param === 'type') {
            this.effectsChain.filter.type = value;
        } else if (param === 'frequency') {
            this.effectsChain.filter.frequency.value = value;
        } else if (param === 'Q') {
            this.effectsChain.filter.Q.value = value;
        }
    }

    updateDelayParam(param, value) {
        this.effectParams.delay[param] = value;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.delay) {
            console.log('Effects chain not initialized, delay parameter saved for later');
            return;
        }

        if (param === 'wet' && this.effectsChain.delay.updateWet) {
            this.effectsChain.delay.updateWet(value);
        } else if (param === 'delayTime' && this.effectsChain.delay.updateDelayTime) {
            this.effectsChain.delay.updateDelayTime(value);
        } else if (param === 'feedback' && this.effectsChain.delay.updateFeedback) {
            this.effectsChain.delay.updateFeedback(value);
        }
    }

    updateCompressorParam(param, value) {
        this.effectParams.compressor[param] = value;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.compressor) {
            console.log('Effects chain not initialized, compressor parameter saved for later');
            return;
        }

        if (param === 'threshold') {
            this.effectsChain.compressor.threshold.value = value;
        } else if (param === 'ratio') {
            this.effectsChain.compressor.ratio.value = value;
        } else if (param === 'attack') {
            this.effectsChain.compressor.attack.value = value;
        } else if (param === 'release') {
            this.effectsChain.compressor.release.value = value;
        }
    }

    updateEQParam(band, gain) {
        this.effectParams.eq.gains[band] = gain;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.eq) {
            console.log('Effects chain not initialized, EQ parameter saved for later');
            return;
        }

        if (this.effectsChain.eq.updateBand) {
            this.effectsChain.eq.updateBand(band, gain);
        }
    }

    updateReverbParam(param, value) {
        this.effectParams.reverb[param] = value;

        // Проверяем, что цепочка эффектов инициализирована
        if (!this.isInitialized || !this.effectsChain || !this.effectsChain.reverb) {
            console.log('Effects chain not initialized, reverb parameter saved for later');
            return;
        }

        if (param === 'wet' && this.effectsChain.reverb.updateWet) {
            this.effectsChain.reverb.updateWet(value);
        } else if (param === 'decay') {
            // Для изменения decay нужно перегенерировать импульсный ответ
            this.generateImpulseResponse(value).then(impulse => {
                if (this.effectsChain.reverb && this.effectsChain.reverb.convolver) {
                    this.effectsChain.reverb.convolver.buffer = impulse;
                }
            });
        }
    }

    updateInputGain(value) {
        this.effectParams.inputGain = value;
        if (this.input) {
            this.input.gain.value = value;
        }

    }

    updateOutputGain(value) {
        this.effectParams.outputGain = value;
        if (this.effectsChain.output) {
            this.effectsChain.output.gain.value = value;
        }

    }

    async initialize(audioContext = null, presetData = null) {
        if (this.isInitialized) return;

        try {
            // Используем общий AudioContextManager если не передан конкретный
            let contextToUse = audioContext || window.audioContextManager.getAudioContext();

            // Если контекст закрыт, получаем новый из AudioContextManager
            if (contextToUse.state === 'closed') {
                console.warn('AudioContext is closed, getting new one from AudioContextManager');
                contextToUse = window.audioContextManager.getAudioContext();
                
                // Если новый контекст тоже закрыт, создаем новый
                if (contextToUse.state === 'closed') {
                    console.warn('AudioContextManager returned closed context, creating new one');
                    contextToUse = new AudioContext();
                }
            }

            // Если AudioContext изменился, сбрасываем флаг загрузки worklet
            if (this.audioContext && this.audioContext !== contextToUse) {
                this.workletLoaded = false;
            }

            this.audioContext = contextToUse;

            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            await this.loadAudioWorklets();

            // Создаем базовую цепочку эффектов
            this.createEffectsChain();

            // Настройки эффектов теперь загружаются только через API (loadEffectsFromServer)
            // Не загружаем из localStorage

            this.isInitialized = true;
            
            // Применяем сохраненные параметры эффектов (если они были загружены с сервера до инициализации)
            this.applyEffectParams();
            
            console.log('Guitar effects processor initialized with preset');

        } catch (error) {
            console.error('Error initializing guitar effects:', error);
            errorLogger.logCustomError(
                `Error initializing guitar effects: ${error.message}`,
                error.stack,
                { errorName: error.name, audioContextState: audioContext?.state }
            );
            throw error;
        }
    }

    async loadAudioWorklets() {
        try {
            // Проверяем, не загружен ли уже worklet
            if (this.workletLoaded) {
                console.log('AudioWorklets already loaded, skipping');
                return;
            }

            if (!this.audioContext.audioWorklet) {
                console.warn('AudioWorklet not supported, using fallback');
                this.workletLoaded = true;
                return;
            }

            const noiseGateWorkletCode = `
                class NoiseGateProcessor extends AudioWorkletProcessor {
                    constructor() {
                        super();
                        this.isOpen = false;
                        this.gain = 1.0;
                        this.threshold = 0.02;
                        this.releaseTime = 0.1;
                        this.sampleRate = 44100;
                        this.releaseSamples = this.releaseTime * this.sampleRate;
                        this.samplesSinceClose = 0;
                    }

                    static get parameterDescriptors() {
                        return [
                            {
                                name: 'threshold',
                                defaultValue: 0.02,
                                minValue: 0.001,
                                maxValue: 0.1
                            },
                            {
                                name: 'releaseTime',
                                defaultValue: 0.1,
                                minValue: 0.01,
                                maxValue: 1.0
                            }
                        ];
                    }

                    process(inputs, outputs, parameters) {
                        const input = inputs[0];
                        const output = outputs[0];
                        
                        if (input.length === 0 || !input[0]) {
                            return true;
                        }

                        const threshold = parameters.threshold[0];
                        const releaseTime = parameters.releaseTime[0];
                        
                        if (releaseTime !== this.releaseTime) {
                            this.releaseTime = releaseTime;
                            this.releaseSamples = releaseTime * this.sampleRate;
                        }

                        const channelData = input[0];
                        let sum = 0;

                        for (let i = 0; i < channelData.length; i++) {
                            sum += channelData[i] * channelData[i];
                        }
                        const rms = Math.sqrt(sum / channelData.length);

                        if (rms > threshold) {
                            this.isOpen = true;
                            this.gain = 1.0;
                            this.samplesSinceClose = 0;
                        } else if (this.isOpen) {
                            this.samplesSinceClose += channelData.length;
                            if (this.samplesSinceClose >= this.releaseSamples) {
                                this.isOpen = false;
                                this.gain = 0.0;
                            } else {
                                this.gain = 1.0 - (this.samplesSinceClose / this.releaseSamples);
                            }
                        }

                        for (let channel = 0; channel < output.length; channel++) {
                            const inputChannel = input[channel];
                            const outputChannel = output[channel];
                            
                            if (inputChannel && outputChannel) {
                                for (let i = 0; i < inputChannel.length; i++) {
                                    outputChannel[i] = inputChannel[i] * this.gain;
                                }
                            }
                        }

                        return true;
                    }
                }
                // Обертываем registerProcessor в try-catch для обработки ошибки повторной регистрации
                try {
                    registerProcessor('noise-gate-processor', NoiseGateProcessor);
                } catch (error) {
                    // Если процессор уже зарегистрирован, это не критично - просто игнорируем
                    // Ошибка будет отправлена на сервер через глобальный обработчик ошибок
                    if (error.name !== 'NotSupportedError' || !error.message.includes('already registered')) {
                        throw error; // Пробрасываем другие ошибки
                    }
                }
            `;

            const blob = new Blob([noiseGateWorkletCode], { type: 'application/javascript' });
            const url = URL.createObjectURL(blob);
            
            try {
                await this.audioContext.audioWorklet.addModule(url);
                this.workletLoaded = true;
                console.log('AudioWorklets loaded successfully');
            } catch (addModuleError) {
                // Обрабатываем ошибки загрузки модуля, включая ошибки из AudioWorklet контекста
                console.error('Error in addModule:', addModuleError);
                
                // Проверяем, не связана ли ошибка с повторной регистрацией процессора
                const errorMessage = addModuleError.message || String(addModuleError);
                const isAlreadyRegistered = errorMessage.includes('already registered') || 
                                           errorMessage.includes('noise-gate-processor');
                
                if (isAlreadyRegistered) {
                    // Если процессор уже зарегистрирован, это не критично
                    console.warn('AudioWorklet processor already registered, continuing...');
                    this.workletLoaded = true;
                } else {
                    // Для других ошибок отправляем на сервер
                    errorLogger.logCustomError(
                        `Error loading AudioWorklet module: ${addModuleError.message || String(addModuleError)}`,
                        addModuleError.stack,
                        { 
                            errorName: addModuleError.name,
                            errorMessage: errorMessage,
                            audioContextState: this.audioContext?.state,
                            workletAlreadyLoaded: this.workletLoaded
                        }
                    );
                    this.workletLoaded = false;
                    throw addModuleError; // Пробрасываем дальше для обработки внешним catch
                }
            } finally {
                URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error('Error loading AudioWorklets:', error);
            errorLogger.logCustomError(
                `Error loading AudioWorklets: ${error.message}`,
                error.stack,
                { 
                    errorName: error.name, 
                    audioContextState: this.audioContext?.state,
                    workletAlreadyLoaded: this.workletLoaded
                }
            );
            // Не сбрасываем workletLoaded, если ошибка связана с повторной регистрацией
            if (error.name === 'NotSupportedError' && error.message.includes('already registered')) {
                this.workletLoaded = true; // Считаем, что worklet уже загружен
            } else if (!this.workletLoaded) {
                this.workletLoaded = false;
            }
        }
    }

    createEffectsChain() {
        if (!this.audioContext) {
            console.error('AudioContext not initialized');
            return;
        }

        // Проверяем, что audioContext не закрыт
        if (this.audioContext.state === 'closed') {
            console.error('Cannot create effects chain: AudioContext is closed');
            errorLogger.logCustomError(
                'Cannot create effects chain: AudioContext is closed',
                new Error().stack,
                { audioContextState: this.audioContext.state }
            );
            return;
        }

        // Создаем входной гейн
        this.input = this.audioContext.createGain();
        this.input.gain.value = 1.0;

        // Создаем эффекты
        this.effectsChain = {
            noiseGate: this.createNoiseGate(),
            distortion: this.createDistortion({ distortion: 1 }),
            filter: this.audioContext.createBiquadFilter(),
            delay: this.createDelay({ delayTime: 0.3, feedback: 0.4, wet: 0.2 }),
            reverb: this.createReverb({ decay: 2.5, wet: 0.3 }),
            eq: this.create10BandEQ(),
            compressor: this.audioContext.createDynamicsCompressor(),
            output: this.audioContext.createGain()
        };

        // Начальная настройка
        this.effectsChain.filter.type = 'lowpass';
        this.effectsChain.filter.frequency.value = 800;
        this.effectsChain.compressor.threshold.value = -24;
        this.effectsChain.compressor.ratio.value = 4;
        this.effectsChain.compressor.attack.value = 0.003;
        this.effectsChain.compressor.release.value = 0.25;
        this.effectsChain.output.gain.value = 1.0;

        // Подключаем цепочку
        this.connectEffectsChain();
        console.log('Base effects chain created and connected');
    }

    createNoiseGate() {
        if (!this.workletLoaded) {
            console.warn('AudioWorklet not loaded, using basic gain node as noise gate');
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = 1.0;
            return gainNode;
        }

        try {
            const noiseGate = new AudioWorkletNode(this.audioContext, 'noise-gate-processor', {
                parameterData: {
                    threshold: this.effectParams.noiseGate.threshold,
                    releaseTime: this.effectParams.noiseGate.releaseTime
                }
            });

            noiseGate.updateThreshold = (value) => {
                noiseGate.parameters.get('threshold').value = value;
            };

            return noiseGate;
        } catch (error) {
            console.error('Error creating AudioWorkletNode for noise gate:', error);
            errorLogger.logCustomError(
                `Error creating AudioWorkletNode for noise gate: ${error.message}`,
                error.stack,
                { errorName: error.name, workletLoaded: this.workletLoaded }
            );
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = 1.0;
            return gainNode;
        }
    }

    create10BandEQ() {
        const frequencies = [31, 62, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
        const filters = [];

        let previousFilter = null;

        frequencies.forEach(freq => {
            const filter = this.audioContext.createBiquadFilter();
            filter.type = 'peaking';
            filter.frequency.value = freq;
            filter.Q.value = 1;
            filter.gain.value = 0;

            if (previousFilter) {
                previousFilter.connect(filter);
            }
            previousFilter = filter;
            filters.push(filter);
        });

        return {
            input: filters[0],
            output: filters[filters.length - 1],
            filters: filters,
            updateBand: (band, gain) => {
                if (filters[band]) {
                    filters[band].gain.value = gain;
                }
            }
        };
    }

    createDistortion(params) {
        const distortion = this.audioContext.createWaveShaper();
        distortion.curve = this.makeDistortionCurve(params.distortion * 100);
        distortion.oversample = '4x';
        return distortion;
    }

    createDelay(params) {
        const delayInput = this.audioContext.createGain();
        const delayOutput = this.audioContext.createGain();
        const dryGain = this.audioContext.createGain();
        const wetGain = this.audioContext.createGain();

        const delay = this.audioContext.createDelay();
        delay.delayTime.value = params.delayTime;

        const feedback = this.audioContext.createGain();
        feedback.gain.value = params.feedback;

        // Соединения для дилея с обратной связью
        delayInput.connect(dryGain);
        delayInput.connect(delay);
        delay.connect(wetGain);
        delay.connect(feedback);
        feedback.connect(delay);

        dryGain.connect(delayOutput);
        wetGain.connect(delayOutput);

        // Настраиваем уровни
        dryGain.gain.value = 1 - params.wet;
        wetGain.gain.value = params.wet;

        return {
            input: delayInput,
            output: delayOutput,
            updateWet: (value) => {
                dryGain.gain.value = 1 - value;
                wetGain.gain.value = value;
            },
            updateFeedback: (value) => {
                feedback.gain.value = value;
            },
            updateDelayTime: (value) => {
                delay.delayTime.value = value;
            }
        };
    }

    createReverb(params) {
        const reverbInput = this.audioContext.createGain();
        const reverbOutput = this.audioContext.createGain();
        const dryGain = this.audioContext.createGain();
        const wetGain = this.audioContext.createGain();

        // Создаем импульсный ответ для реверберации
        const convolver = this.audioContext.createConvolver();
        this.generateImpulseResponse(params.decay).then(impulse => {
            convolver.buffer = impulse;
        });

        // Соединения
        reverbInput.connect(dryGain);
        reverbInput.connect(convolver);
        convolver.connect(wetGain);

        dryGain.connect(reverbOutput);
        wetGain.connect(reverbOutput);

        // Настраиваем уровни
        dryGain.gain.value = 1 - params.wet;
        wetGain.gain.value = params.wet;

        return {
            input: reverbInput,
            output: reverbOutput,
            updateWet: (value) => {
                dryGain.gain.value = 1 - value;
                wetGain.gain.value = value;
            },
            convolver: convolver
        };
    }

    async generateImpulseResponse(duration = 2.5) {
        const sampleRate = this.audioContext.sampleRate;
        const length = sampleRate * duration;
        const impulse = this.audioContext.createBuffer(2, length, sampleRate);
        const leftChannel = impulse.getChannelData(0);
        const rightChannel = impulse.getChannelData(1);

        for (let i = 0; i < length; i++) {
            leftChannel[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
            rightChannel[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
        }

        return impulse;
    }

    connectEffectsChain() {
        // Отключаем все предыдущие подключения
        this.disconnectEffectsChain();

        try {
            // Подключаем последовательную цепочку
            this.input.connect(this.effectsChain.noiseGate);
            this.effectsChain.noiseGate.connect(this.effectsChain.distortion);
            this.effectsChain.distortion.connect(this.effectsChain.filter);
            this.effectsChain.filter.connect(this.effectsChain.delay.input);
            this.effectsChain.delay.output.connect(this.effectsChain.compressor);
            this.effectsChain.compressor.connect(this.effectsChain.eq.input);
            this.effectsChain.eq.output.connect(this.effectsChain.reverb.input);
            this.effectsChain.reverb.output.connect(this.effectsChain.output);
            
            // Подключаем к нормализатору вместо прямого подключения к destination
            audioNormalizer.initialize(this.audioContext);
            audioNormalizer.normalizeGuitar(this.effectsChain.output);
            const normalizerInput = audioNormalizer.getWebAudioInput(this.audioContext);
            if (normalizerInput) {
                this.effectsChain.output.connect(normalizerInput);
            } else {
                // Fallback: прямое подключение, если нормализатор не работает
                this.effectsChain.output.connect(this.audioContext.destination);
            }

            console.log('Effects chain connected successfully');
        } catch (error) {
            console.error('Error connecting effects chain:', error);
            errorLogger.logCustomError(
                `Error connecting effects chain: ${error.message}`,
                error.stack,
                { errorName: error.name, isInitialized: this.isInitialized }
            );
        }
    }

    disconnectEffectsChain() {
        // Отключаем вход от всех узлов
        if (this.input) {
            this.input.disconnect();
        }

        // Отключаем все эффекты в основной цепочке
        Object.values(this.effectsChain).forEach(effect => {
            if (effect && typeof effect.disconnect === 'function') {
                effect.disconnect();
            }
        });
    }

    async connectMicrophone(stream, audioContext = null) {
        // Если передан новый audioContext, обновляем его
        if (audioContext && audioContext !== this.audioContext) {
            this.audioContext = audioContext;
            this.workletLoaded = false; // Сбрасываем флаг при смене контекста
            await this.reinitializeWithCurrentSettings();
        }

        if (!this.isInitialized) {
            await this.initialize(this.audioContext);
        }

        // Останавливаем существующий источник
        if (this.micSource) {
            this.micSource.disconnect();
        }

        try {
            // Создаем источник из микрофона ДЛЯ ЭФФЕКТОВ
            this.micSource = this.audioContext.createMediaStreamSource(stream);

            // Подключаем только к цепочке эффектов (не к анализатору тюнера)
            this.micSource.connect(this.input);

            console.log('Microphone connected to effects chain only');
            // Не изменяем состояние чекбокса эффектов при подключении микрофона
            // Используем текущее состояние isEffectsEnabled без изменения UI

        } catch (error) {
            console.error('Error connecting microphone to effects:', error);
            errorLogger.logCustomError(
                `Error connecting microphone to effects: ${error.message}`,
                error.stack,
                { errorName: error.name, isInitialized: this.isInitialized }
            );
            throw error;
        }
    }

    // Подключение Tone.js источника (например, семплера) к цепочке эффектов
    async connectToneSource(toneSource) {
        if (!this.isInitialized || !this.input) {
            console.warn('Effects chain not initialized, cannot connect Tone.js source');
            return false;
        }

        if (!toneSource) {
            console.error('Tone.js source is null or undefined');
            return false;
        }

        try {
            // Получаем внутренний контекст Tone.js
            const toneContext = Tone.getContext();
            if (!toneContext || !toneContext.rawContext) {
                console.error('Cannot get Tone.js audio context');
                return false;
            }

            // КРИТИЧНО: Убеждаемся, что используем один и тот же AudioContext
            if (toneContext.rawContext !== this.audioContext) {
                console.warn('⚠️ Tone.js context differs from effects context, reinitializing effects with Tone context');
                // Переинициализируем эффекты с Tone.js контекстом
                await this.initialize(toneContext.rawContext);
            }

            // Tone.js источники имеют метод connect, который может принимать Web Audio API узлы
            // Отключаем от всех предыдущих подключений
            if (typeof toneSource.disconnect === 'function') {
                toneSource.disconnect();
            }

            // Подключаем Tone.js источник напрямую к входу эффектов
            // Теперь оба используют один AudioContext
            toneSource.connect(this.input);
            console.log('✅ Tone.js source connected to effects chain');

            return true;
        } catch (error) {
            console.error('❌ Error connecting Tone.js source to effects:', error);
            errorLogger.logCustomError(
                `Error connecting Tone.js source to effects: ${error.message}`,
                error.stack,
                { errorName: error.name, isInitialized: this.isInitialized }
            );
            return false;
        }
    }

    // Отключение Tone.js источника от цепочки эффектов
    disconnectToneSource(toneSource) {
        if (!toneSource) return;

        try {
            // Отключаем Tone.js источник от входа эффектов
            if (typeof toneSource.disconnect === 'function') {
                // Отключаем от конкретного узла, если возможно
                try {
                    toneSource.disconnect(this.input);
                } catch (e) {
                    // Если не удалось отключить от конкретного узла, отключаем от всех
                    toneSource.disconnect();
                }
                console.log('Tone.js source disconnected from effects chain');
            }
        } catch (error) {
            console.error('Error disconnecting Tone.js source:', error);
        }
    }

    // Добавьте этот метод в класс GuitarEffectsProcessor
    async reinitializeWithCurrentSettings() {
        if (this.isInitialized && this.audioContext) {
            // Проверяем, что audioContext не закрыт
            if (this.audioContext.state === 'closed') {
                console.error('Cannot reinitialize: AudioContext is closed');
                errorLogger.logCustomError(
                    'Cannot reinitialize: AudioContext is closed',
                    new Error().stack,
                    { audioContextState: this.audioContext.state }
                );
                this.isInitialized = false;
                return;
            }

            // Сохраняем текущие настройки
            const currentSettings = this.getAllSettings();

            // Отключаем старую цепочку (но НЕ закрываем audioContext)
            this.disconnectEffectsChain();
            this.isInitialized = false;
            this.workletLoaded = false; // Сбрасываем флаг для перезагрузки worklet

            // Создаем новую цепочку с сохраненными настройками
            await this.initialize(this.audioContext);
            this.applyAllSettings(currentSettings);
        }
    }

    toggleEffects(enabled) {
        if (!this.isInitialized || !this.micSource) {
            console.log('Cannot toggle effects - not initialized or no microphone');
            return;
        }

        // Отключаем микрофон от всего
        this.micSource.disconnect();

        if (enabled) {
            // Включаем эффекты - подключаем микрофон к входу цепочки эффектов
            this.micSource.connect(this.input);
            
            // Убеждаемся, что normalizeGuitar применяется при включении эффектов
            if (this.effectsChain && this.effectsChain.output) {
                audioNormalizer.initialize(this.audioContext);
                audioNormalizer.normalizeGuitar(this.effectsChain.output);
            }
            
            console.log('Effects ON - processed sound to speakers');
        } else {
            // Выключаем эффекты - подключаем микрофон напрямую к выходу
            this.micSource.connect(this.audioContext.destination);
            console.log('Effects OFF - clean sound to speakers');
        }

        this.isEffectsEnabled = enabled;

        // Обновляем UI переключателя
        if (this.elements.effectsToggle) {
            this.elements.effectsToggle.checked = enabled;
        }

    }

    updateEffectParameter(effect, parameter, value) {
        if (!this.effectsChain[effect]) {
            console.warn(`Effect ${effect} not found`);
            return;
        }

        try {
            const node = this.effectsChain[effect];

            switch(effect) {
                case 'distortion':
                    if (parameter === 'distortion') {
                        node.curve = this.makeDistortionCurve(value * 100);
                    }
                    break;

                case 'filter':
                    if (parameter === 'frequency') {
                        node.frequency.value = value;
                    }
                    break;

                case 'delay':
                    if (parameter === 'wet' && node.updateWet) {
                        node.updateWet(value);
                    } else if (parameter === 'feedback' && node.updateFeedback) {
                        node.updateFeedback(value);
                    } else if (parameter === 'delayTime' && node.updateDelayTime) {
                        node.updateDelayTime(value);
                    }
                    break;

                case 'reverb':
                    if (parameter === 'wet' && node.updateWet) {
                        node.updateWet(value);
                    }
                    break;

                case 'output':
                    if (parameter === 'volume') {
                        node.gain.value = value;
                    }
                    break;

                default:
                    console.warn(`Parameter ${parameter} for effect ${effect} not implemented`);
            }

            console.log(`Updated ${effect}.${parameter} to ${value}`);
        } catch (error) {
            console.error(`Error updating ${effect}.${parameter}:`, error);
            errorLogger.logCustomError(
                `Error updating effect parameter: ${effect}.${parameter} - ${error.message}`,
                error.stack,
                { errorName: error.name, effect, parameter, value }
            );
        }
    }

    // Методы для пресетов (упрощенные)
    setVersePreset() {
        console.log('Setting verse preset');
        this.updateEffectParameter('distortion', 'distortion', 0.3);
        this.updateEffectParameter('delay', 'wet', 0.1);
        this.updateEffectParameter('reverb', 'wet', 0.2);
        this.updateEffectParameter('filter', 'frequency', 1000);
        this.updateUIWithCurrentValues();
    }

    setChorusPreset() {
        console.log('Setting chorus preset');
        this.updateEffectParameter('distortion', 'distortion', 0.7);
        this.updateEffectParameter('delay', 'wet', 0.3);
        this.updateEffectParameter('reverb', 'wet', 0.4);
        this.updateEffectParameter('filter', 'frequency', 2000);
        this.updateUIWithCurrentValues();
    }

    setBridgePreset() {
        console.log('Setting bridge preset');
        this.updateEffectParameter('distortion', 'distortion', 0.9);
        this.updateEffectParameter('delay', 'wet', 0.2);
        this.updateEffectParameter('reverb', 'wet', 0.3);
        this.updateEffectParameter('filter', 'frequency', 800);
        this.updateUIWithCurrentValues();
    }

    updateUIWithCurrentValues() {
        // Простая реализация для обновления UI
        setTimeout(() => {
            if (this.elements.distortionEffect) {
                this.elements.distortionEffect.value = 0.5;
            }
            if (this.elements.filterEffect) {
                this.elements.filterEffect.value = 1000;
            }
            if (this.elements.reverbEffect) {
                this.elements.reverbEffect.value = 0.3;
            }
        }, 100);
    }

    // Упрощенные методы для Bias FX2 (базовая поддержка)
    // Обновляем applyBiasFX2Parameters для работы с новой системой
    applyBiasFX2Parameters(presetData) {
        if (!presetData || !presetData.sigPath) return;

        console.log('Applying Bias FX2 parameters with advanced controls');

        // Сбрасываем все эффекты к начальным значениям
        this.resetAllEffects();


        presetData.sigPath.forEach(effect => {
            if (!effect.active) return;

            switch(effect.dspId) {
                case 'FX2.NoiseGate':
                    this.applyNoiseGateParameters(effect.param);
                    break;
                case 'FX2.Distortion':
                    this.applyDistortionParameters(effect.param);
                    break;
                case 'FX2.Delay':
                    this.applyDelayParameters(effect.param);
                    break;
                case 'FX2.DigitalReverb':
                    this.applyReverbParameters(effect.param);
                    break;
                case 'FX2.CompRack':
                    this.applyCompressorParameters(effect.param);
                    break;
                case 'FX2.EQ10':
                    this.applyEQParameters(effect.param);
                    break;
            }
        });

        // Обновляем UI
        this.updateAdvancedUI();
    }

    resetAllEffects() {
        // Сбрасываем все параметры к значениям по умолчанию
        this.effectParams = {
            noiseGate: { threshold: 0.02, releaseTime: 0.1 },
            distortion: { distortion: 0.5, gain: 0.2 },
            filter: { type: 'lowpass', frequency: 2670, Q: 0.4 },
            delay: { wet: 0.3, delayTime: 0.2, feedback: 0.4 },
            compressor: { threshold: -36, ratio: 4, attack: 0.003, release: 0.25 },
            eq: { gains: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
            reverb: { wet: 0.72, decay: 2 },
            inputGain: 1.0,
            outputGain: 1.0
        };

        // Включаем все эффекты
        Object.keys(this.effectsEnabled).forEach(key => {
            this.effectsEnabled[key] = true;
        });
    }

    // Обновите метод updateAdvancedUI для правильного обновления UI
    updateAdvancedUI() {
        // Обновляем все элементы UI в соответствии с текущими параметрами
        setTimeout(() => {
            // Шумоподавитель
            const noiseGateThreshold = document.getElementById('noiseGateThreshold');
            const noiseGateRelease = document.getElementById('noiseGateRelease');
            if (noiseGateThreshold) noiseGateThreshold.value = this.effectParams.noiseGate.threshold;
            if (noiseGateRelease) noiseGateRelease.value = this.effectParams.noiseGate.releaseTime;

            // Дисторшн
            const distortionAmount = document.getElementById('distortionAmount');
            const distortionGain = document.getElementById('distortionGain');
            if (distortionAmount) distortionAmount.value = this.effectParams.distortion.distortion;
            if (distortionGain) distortionGain.value = this.effectParams.distortion.gain;

            // Фильтр
            const filterType = document.getElementById('filterType');
            const filterFrequency = document.getElementById('filterFrequency');
            const filterQ = document.getElementById('filterQ');
            if (filterType) filterType.value = this.effectParams.filter.type;
            if (filterFrequency) filterFrequency.value = this.effectParams.filter.frequency;
            if (filterQ) filterQ.value = this.effectParams.filter.Q;

            // Дилей
            const delayWet = document.getElementById('delayWet');
            const delayTime = document.getElementById('delayTime');
            const delayFeedback = document.getElementById('delayFeedback');
            if (delayWet) delayWet.value = this.effectParams.delay.wet;
            if (delayTime) delayTime.value = this.effectParams.delay.delayTime;
            if (delayFeedback) delayFeedback.value = this.effectParams.delay.feedback;

            // Компрессор
            const compressorThreshold = document.getElementById('compressorThreshold');
            const compressorRatio = document.getElementById('compressorRatio');
            const compressorAttack = document.getElementById('compressorAttack');
            const compressorRelease = document.getElementById('compressorRelease');
            if (compressorThreshold) compressorThreshold.value = this.effectParams.compressor.threshold;
            if (compressorRatio) compressorRatio.value = this.effectParams.compressor.ratio;
            if (compressorAttack) compressorAttack.value = this.effectParams.compressor.attack;
            if (compressorRelease) compressorRelease.value = this.effectParams.compressor.release;

            // Эквалайзер
            this.effectParams.eq.gains.forEach((gain, index) => {
                const slider = document.querySelector(`.eq-band[data-band="${index}"]`);
                if (slider) slider.value = gain;
            });

            // Реверберация
            const reverbWet = document.getElementById('reverbWet');
            const reverbDecay = document.getElementById('reverbDecay');
            if (reverbWet) reverbWet.value = this.effectParams.reverb.wet;
            if (reverbDecay) reverbDecay.value = this.effectParams.reverb.decay;

            // Общие настройки
            const inputGain = document.getElementById('inputGain');
            const outputGain = document.getElementById('outputGain');
            if (inputGain) inputGain.value = this.effectParams.inputGain;
            if (outputGain) outputGain.value = this.effectParams.outputGain;

            // Обновляем отображаемые значения
            this.updateParamDisplayValues();

        }, 100);
    }

    // Добавьте метод для обновления отображаемых значений
    updateParamDisplayValues() {
        const updateDisplay = (sliderId, displayId) => {
            const slider = document.getElementById(sliderId);
            const display = document.getElementById(displayId);
            if (slider && display) {
                display.textContent = slider.value;
            }
        };

        // Обновляем все отображаемые значения
        updateDisplay('noiseGateThreshold', 'noiseGateThresholdValue');
        updateDisplay('noiseGateRelease', 'noiseGateReleaseValue');
        updateDisplay('distortionAmount', 'distortionAmountValue');
        updateDisplay('distortionGain', 'distortionGainValue');
        updateDisplay('filterFrequency', 'filterFrequencyValue');
        updateDisplay('filterQ', 'filterQValue');
        updateDisplay('delayWet', 'delayWetValue');
        updateDisplay('delayTime', 'delayTimeValue');
        updateDisplay('delayFeedback', 'delayFeedbackValue');
        updateDisplay('compressorThreshold', 'compressorThresholdValue');
        updateDisplay('compressorRatio', 'compressorRatioValue');
        updateDisplay('compressorAttack', 'compressorAttackValue');
        updateDisplay('compressorRelease', 'compressorReleaseValue');
        updateDisplay('reverbWet', 'reverbWetValue');
        updateDisplay('reverbDecay', 'reverbDecayValue');
        updateDisplay('inputGain', 'inputGainValue');
        updateDisplay('outputGain', 'outputGainValue');
    }

    applyDistortionParameters(params) {
        if (params && params[0]) {
            const drive = this.mapBiasParam(params[0].value, 0, 1);
            this.updateEffectParameter('distortion', 'distortion', drive);
        }
    }

    applyDelayParameters(params) {
        if (params && params[4]) {
            const mix = this.mapBiasParam(params[4].value, 0, 0.5);
            this.updateEffectParameter('delay', 'wet', mix);
        }
    }

    applyReverbParameters(params) {
        if (params && params[3]) {
            const mix = this.mapBiasParam(params[3].value, 0, 10);
            this.updateEffectParameter('reverb', 'wet', mix);
        }
    }

    // Добавляем методы для применения параметров из Bias FX2
    applyNoiseGateParameters(params) {
        if (params && params[0]) {
            const threshold = this.mapBiasParam(params[0].value, 0.001, 0.1);
            this.updateNoiseGateParam('threshold', threshold);
        }
    }

    applyCompressorParameters(params) {
        if (params && params[1]) {
            const threshold = this.mapBiasParam(params[1].value, -60, 0);
            const ratio = this.mapBiasParam(params[2].value, 1, 20);
            this.updateCompressorParam('threshold', threshold);
            this.updateCompressorParam('ratio', ratio);
        }
    }

    applyEQParameters(params) {
        // Упрощенная реализация для 10-полосного эквалайзера
        if (params && params.length >= 10) {
            for (let i = 0; i < 10; i++) {
                const gain = this.mapBiasParam(params[i].value, -20, 20);
                this.updateEQParam(i, gain);
            }
        }
    }

    mapBiasParam(value, min, max) {
        return min + (value * (max - min));
    }

    async loadPreset(presetName) {
        try {
            const presets = await this.getSavedPresets();
            const preset = presets.find(p => p.name === presetName);

            if (preset) {
                this.currentPreset = preset;
                this.applyBiasFX2Parameters(preset);
                console.log(`Loaded and applied preset: ${presetName}`);
            }
        } catch (error) {
            console.error('Error loading preset:', error);
            errorLogger.logCustomError(
                `Error loading preset: ${error.message}`,
                error.stack,
                { errorName: error.name, presetName }
            );
        }
    }

    async getSavedPresets() {
        try {
            const response = await fetch('./data.json');
            return await response.json();
        } catch (error) {
            console.error('Failed to load presets:', error);
            errorLogger.logCustomError(
                `Failed to load presets: ${error.message}`,
                error.stack,
                { errorName: error.name }
            );
            return [];
        }
    }

    disconnect() {
        if (this.micSource) {
            this.micSource.disconnect();
            this.micSource = null;
        }

        // Отключаем все узлы
        this.disconnectEffectsChain();

        // НЕ закрываем audioContext, так как он может использоваться другими компонентами
        // (например, тюнером или другими частями приложения)
        // AudioContext должен управляться централизованно через AudioContextManager

        this.isInitialized = false;
        console.log('Guitar effects disconnected');
    }
}

// Создаем и экспортируем экземпляр
const guitarEffects = new GuitarEffectsProcessor();

// Функции для интеграции
export const initializeGuitarEffects = async (stream, audioContext = null) => {
    await guitarEffects.initialize(audioContext);
    await guitarEffects.connectMicrophone(stream);
};

export const disconnectEffects = () => guitarEffects.disconnect();

export { guitarEffects, GuitarEffectsProcessor };